"use client";

import { FC, useCallback, useEffect, useState } from "react";

import { isEditing } from "@builder.io/sdk-react";
import Script from "next/script";

import { getCookie } from "@jti/fe-common/utils";
import { getUser } from "@jti/rest-api";
import { useConfig } from "@jti/ui/providers/useConfig";

import { getSavLiveChatConfig } from "@/app/_internal/getSavLiveChatConfig";
import { useLocale } from "@/context/LocaleContext";

import { liveChatInputs } from "./LiveChat.builder";

export type LiveChatProps = BuilderComponentProps<typeof liveChatInputs>;

type LoadFeature = {
  feature: string;
  openOnLoad: boolean;
  params: {
    language: string;
    sysparm_vaMarket: string;
    sysparm_consumer_cas_uuid?: string;
  };
};

declare global {
  interface Window {
    SN_CSM_EC: {
      init: ({
        moduleID,
        loadFeature,
        tokenCallBack,
      }: {
        moduleID: string;
        loadFeature: LoadFeature;
        tokenCallBack?: () => void;
        lang: string;
      }) => void;
      loadEMFeature(): LoadFeature;
    };
  }
}

const initChatWidget = ({
  languageCode,
  moduleId,
}: {
  languageCode: string;
  moduleId: string;
}): void => {
  const getTokenCallBack = () => {
    const user = getUser();
    Promise.resolve(user.ciam_token || null);
  };

  if (typeof window.SN_CSM_EC === "undefined") {
    return;
  }

  const { uuid } = getUser();
  const contextObject = window.SN_CSM_EC.loadEMFeature();
  contextObject.openOnLoad = false; // default is true, so we need to overwrite to keep the chat closed
  contextObject.params.language = languageCode;
  contextObject.params.sysparm_vaMarket = languageCode;

  if (uuid) {
    contextObject.params.sysparm_consumer_cas_uuid = uuid;
  }

  window.SN_CSM_EC.init({
    moduleID: moduleId,
    loadFeature: contextObject,
    tokenCallBack: getTokenCallBack,
    lang: languageCode,
  });
};

export const LiveChat: FC<LiveChatProps> = ({
  enabled,
  configuration: { scriptSrc, languageCode, moduleId },
}) => {
  const { currentLocale } = useLocale();

  const [isChatAvailable, setIsChatAvailable] = useState<boolean>(false);

  useEffect(() => {
    const fetchSavLiveChatConfigData = async () => {
      const requiresSav = await getSavLiveChatConfig({ locale: currentLocale });
      const isAgeConfirmed = getCookie("isAgeConfirmed") || "";

      if (requiresSav && isAgeConfirmed) {
        setIsChatAvailable(true);
      }

      if (requiresSav && !isAgeConfirmed) {
        setIsChatAvailable(false);
      }

      if (!requiresSav) {
        setIsChatAvailable(true);
      }
    };

    fetchSavLiveChatConfigData();
  }, [currentLocale]);

  const { liveChatLazyLoading: lazyLoad = false } = useConfig();

  const handleScriptLoad = useCallback(() => {
    initChatWidget({ languageCode, moduleId });
  }, [languageCode, moduleId]);

  useEffect(() => {
    if (!lazyLoad) {
      handleScriptLoad();
    }
  }, [lazyLoad, handleScriptLoad]);

  const strategy = lazyLoad ? "lazyOnload" : "beforeInteractive";

  return (
    <>
      {isEditing() && "LiveChat authoring placeholder"}
      {!isEditing() && enabled && isChatAvailable && (
        <Script
          src={scriptSrc}
          strategy={strategy}
          onLoad={() => lazyLoad && handleScriptLoad()}
        ></Script>
      )}
    </>
  );
};
